import 'date-fns';
import { Component } from 'react';
import classnames from 'classnames';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  FormControl,
  FormHelperText,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Theme, styled } from "@mui/material/styles";
import { parse } from 'date-fns';

// Context
import { withTranslate } from '../../../core/TenantProvider';
// Files in the same folder
import ErrorIcon from './assets/icon-error.svg';
import ValidIcon from './assets/icon-tick-outline.svg';
import { ReactComponent as SwitchArrowIcon } from './assets/actions-navigation-chevron-down-icon.svg';
import { ReactComponent as LeftArrowIcon } from './assets/actions-navigation-chevron-left-icon.svg';
import { ReactComponent as RightArrowIcon } from './assets/actions-navigation-chevron-right-icon.svg';
import { ReactComponent as CalendarIcon } from './assets/planning-time-calendar-icon.svg';
import APInputStyles from './styles/APInput.style';
// Types
import { HexColour } from '../../../core/types/ThemeConfigTypes';
// Utils
import {
  getBody2NormalStyles,
  getBodyExtraSmallBoldStyles,
  getBodyExtraSmallNormalStyles,
  getBodySmallBoldStyles
} from '../../../core/utils/GetTypographyStyles/GetTypographyStyles';
//Constants
import { MASK_CLASS } from '../../../core/utils/Constants/Constants';

type Varient = 'annual_statement'

interface IconProps {
  disabled: boolean;
  error: boolean;
}

const styles = (theme: Theme) => {
  const {
    colours: { backgrounds, borders, text }
  } = theme;

  const COLOUR_BG_BACKGROUND_ALT: HexColour = backgrounds.backgroundAlt;
  const COLOUR_BORDER_BORDER_INPUT: HexColour = borders.borderInput;
  const COLOUR_BG_BACKGROUND_ALT_INVERSE_WEAK: HexColour = backgrounds.backgroundAltInverseWeak;
  const COLOUR_TEXT_TEXT_LINK: HexColour = text.textLink;
  const COLOUR_TEXT_TEXT_INVERSE: HexColour = text.textInverse;
  const COLOUR_TEXT_TEXT_WEAKEST: HexColour = text.textWeakest;

  return {
    // MuiPaper-root
    backgroundColor: COLOUR_BG_BACKGROUND_ALT,
    boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.15)',
    borderRadius: '8px',

    // to remove ripple effect
    '.MuiTouchRipple-root': {
      display: 'none'
    },

    // for the mobile version of the datepicker
    '.MuiDialog-paper': {
      boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.15)'
    },

    // Container for prev next icons and year dropdown
    '.MuiCalendarPicker-root': {
      width: 'unset',
      maxHeight: 'none'
    },

    '.MuiCalendarOrClockPicker-root > div:nth-of-type(1) ': {
      maxHeight: 'none'
    },
    '.MuiCalendarPicker-root > div:nth-of-type(1) ': {
      margin: 0,
      padding: '16px 12px 12px'
    },

    // Prev next arrow icons
    '.MuiPickersArrowSwitcher-root': {
      position: 'absolute',
      width: '92.5%',
      justifyContent: 'space-between',

      '> button': {
        borderRadius: '8px',
        backgroundColor: COLOUR_BG_BACKGROUND_ALT_INVERSE_WEAK,
        color: COLOUR_TEXT_TEXT_LINK,
        padding: '4px',

        ':hover': {
          backgroundColor: COLOUR_BG_BACKGROUND_ALT_INVERSE_WEAK
        }
      }
    },

    '.MuiPickersArrowSwitcher-spacer': {
      display: 'none'
    },

    // Year dropdown
    'div[role="presentation"]': {
      backgroundColor: COLOUR_BG_BACKGROUND_ALT,
      border: `1px solid ${COLOUR_BORDER_BORDER_INPUT}`,
      borderRadius: '4px',
      padding: '4px 16px',
      ...getBody2NormalStyles(theme),
      margin: '0 auto',
      zIndex: 2,

      '> button': {
        padding: 0,

        ':hover': {
          backgroundColor: 'transparent'
        }
      }
    },

    // Year and month picker
    '.PrivatePickersYear-root .PrivatePickersYear-yearButton, .PrivatePickersMonth-root': {
      ...getBody2NormalStyles(theme),
      borderRadius: '8px',

      ':hover': {
        backgroundColor: 'transparent',
        color: COLOUR_TEXT_TEXT_LINK
      }
    },

    // Selected option for month and year
    '.PrivatePickersYear-root .Mui-selected, .PrivatePickersMonth-root.Mui-selected': {
      backgroundColor: COLOUR_TEXT_TEXT_LINK,
      color: COLOUR_TEXT_TEXT_INVERSE,
      ':hover, :focus': {
        backgroundColor: COLOUR_TEXT_TEXT_LINK,
        color: COLOUR_TEXT_TEXT_INVERSE
      }
    },

    // Disabled months and years
    '.PrivatePickersYear-root .Mui-disabled, .PrivatePickersYear-root .Mui-disabled:hover, .PrivatePickersMonth-root.Mui-disabled, .PrivatePickersMonth-root.Mui-disabled:hover':
    {
      color: COLOUR_TEXT_TEXT_WEAKEST
    },

    // Day heading
    '.MuiTypography-caption': {
      width: '40px',
      ...getBodyExtraSmallBoldStyles(theme)
    },

    // Date picker
    '.PrivatePickersSlideTransition-root': {
      minHeight: '255px'
    },

    // Days from 1-31
    '.PrivatePickersSlideTransition-root .MuiPickersDay-root': {
      ...getBodyExtraSmallNormalStyles(theme),
      width: '40px',
      height: '40px',
      borderRadius: '8px',
      backgroundColor: 'transparent',
      margin: '0 2px',

      ':hover': {
        backgroundColor: 'transparent',
        color: COLOUR_TEXT_TEXT_LINK,
        orderRadius: 4
      }
    },

    // Today
    '.PrivatePickersSlideTransition-root .MuiPickersDay-today': {
      border: 'none',
      color: COLOUR_TEXT_TEXT_LINK
    },

    // Selected date
    '.PrivatePickersSlideTransition-root .Mui-selected': {
      color: COLOUR_TEXT_TEXT_INVERSE,
      backgroundColor: COLOUR_TEXT_TEXT_LINK,

      ':hover, :focus': {
        color: COLOUR_TEXT_TEXT_INVERSE,
        backgroundColor: COLOUR_TEXT_TEXT_LINK,
        orderRadius: 4
      }
    },

    // Disabled dates
    '.PrivatePickersSlideTransition-root .Mui-disabled': {
      color: COLOUR_TEXT_TEXT_WEAKEST,

      ':hover': {
        color: COLOUR_TEXT_TEXT_WEAKEST
      }
    },

    // Clear button
    '.MuiDialogActions-root ': {
      padding: '10px 10px 16px',
      '.MuiButton-root': {
        width: '100%',
        backgroundColor: COLOUR_BG_BACKGROUND_ALT_INVERSE_WEAK,
        ...getBodySmallBoldStyles(theme),
        padding: '4px'
      }
    }
  };
};

const CustomTextField = styled(TextField)(({ theme, disabled, error, size }) => {
  return {
    '& .MuiFormHelperText-root.Mui-error': {
      marginLeft: 0
    }
  }
});


const ArrowIconContainer = styled('span')(({ theme }) => {
  const {
    colours: { text }
  } = theme;

  const COLOUR_TEXT_TEXT_LINK: HexColour = text.textLink;

  return {
    '> svg > path': {
      fill: COLOUR_TEXT_TEXT_LINK
    }
  };
});

const IconContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'disabled' && prop !== 'error'
})<IconProps>(({ theme, disabled, error }) => {
  const {
    colours: { text, icon }
  } = theme;

  const COLOUR_TEXT_TEXT_LINK: HexColour = icon.icon;
  const COLOUR_TEXT_TEXT_ERROR: HexColour = text.textError;
  const COLOUR_TEXT_TEXT_INPUT_DISABLED: HexColour = text.textInputDisabled;

  let fillColor: HexColour = COLOUR_TEXT_TEXT_LINK;

  if (error) {
    fillColor = COLOUR_TEXT_TEXT_ERROR;
  } else if (disabled) {
    fillColor = COLOUR_TEXT_TEXT_INPUT_DISABLED;
  }
  return {
    display: 'flex',
    alignItems: 'center',
    cursor: disabled ? 'unset' : 'pointer',

    '> svg > path': {
      fill: fillColor
    }
  };
});

class APDOBInput extends Component<any, {}> {
  state = {
    touched: false,
    isOpen: false,
    errorType: null
  };

  constructor(props: any) {
    super(props);

    this.onChange = this.onChange.bind(this);
  }

  onChange(value: Date | null) {
    const { name, onChange } = this.props;

    this.setState({ touched: true });

    if (value === null) {
      if (onChange) {
        onChange({
          target: {
            name,
            value: null
          }
        });
      }
      return;
    }

    if (onChange && value instanceof Date && !isNaN(value.getTime())) {
      onChange({
        target: {
          name,
          value
        }
      });
    }
  }

  renderErrorMessage = (error) => {
    const {
      translate,
      varient
    } = this.props;

    switch (error) {
      case 'maxDate':
      case 'disableFuture': {
        return translate(varient === 'annual_statement' ? 'register.customer.form.tax.year.limit.error' : 'register.customer.form.dateofbirth.age.limit.error');
      }

      case 'minDate': {
        return translate(varient === 'annual_statement' ? 'register.customer.form.tax.year.limit.error' : 'register.customer.form.dateofbirth.age.limit.error');
      }

      case 'invalidDate': {
        return translate(varient === 'annual_statement' ? 'register.customer.form.annual_statement.date.error' : 'register.customer.form.dateofbirth.error');
      }

      default: {
        return '';
      }
    }
  }

  render() {
    const {
      name,
      classes,
      className,
      error: errorState,
      helperText,
      value,
      translate,
      isClearable = true,
      size,
      onBlur,
      isDisabled = false,
      id,
      placeholderLabel,
      onChange,
      views = ['year', 'month', 'day'],
      isCoralogixMasked = false,
      inputFormatType = "dd/MM/yyyy",
      minDate,
      maxDate,
      setErrorValue
    } = this.props;

    const error = !!this.state.errorType || errorState;
    const controlCls = classnames(classes.formControl, classes.dobField, className);
    const d: any = new Date();
    const date: number = d.getDate();
    const month: number = d.getMonth() + 1;
    const maximumDOBYear: number = d.getFullYear() - 18;
    const minimumDOBYear: number = d.getFullYear() - 100;
    const minimumDate: any = minDate ?? minimumDOBYear;
    const maximumDate: Date | undefined = maxDate === null ? undefined : (maxDate ?? `${maximumDOBYear}-${month}-${date}`);

    const errorMessage = this.renderErrorMessage(this.state.errorType);
    const shouldShowError = error ?? !!this.state.errorType;

    const parsedValue = !!onChange
      ? views?.length === 1 && views[0] === 'year'
        ? typeof value === 'string'
          ? parse(value, 'yyyy', new Date())
          : value
        : value
      : undefined;

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <FormControl className={controlCls}>
          <DatePicker
            disableFuture
            open={this.state.isOpen}
            onClose={() => { this.setState({ isOpen: false }) }}
            label={placeholderLabel ?? translate('register.customer.form.dateofbirth.label')}
            value={parsedValue}
            onChange={this.onChange}
            closeOnSelect={true}
            views={views}
            inputFormat={inputFormatType}
            minDate={minimumDate}
            maxDate={maximumDate}
            renderInput={(params: TextFieldProps) => (
              <CustomTextField
                {...params}
                {...isCoralogixMasked && { className: MASK_CLASS }}
                variant="outlined"
                type="date"
                size={size === 'normal' ? 'medium' : size}
                disabled={isDisabled}
                name={name}
                error={!!error}
                onBlur={onBlur}
                InputProps={{
                  endAdornment: (
                    <IconContainer
                      disabled={isDisabled}
                      error={!!error}
                      onClick={() => !isDisabled && this.setState({ isOpen: true })}
                      data-testid="ae-date-picker-icon-button">
                      <CalendarIcon />
                    </IconContainer>
                  ),
                  autoComplete: 'off'
                }}
                id={id}
                helperText={
                  shouldShowError && <FormHelperText className={classes.helperText} error={shouldShowError}>
                    {this.state.errorType ? errorMessage : helperText}
                  </FormHelperText>
                }
              />
            )}
            showToolbar={false}
            disabled={isDisabled}
            toolbarPlaceholder=""
            components={{
              RightArrowIcon: () => (
                <ArrowIconContainer>
                  <RightArrowIcon />
                </ArrowIconContainer>
              ),
              LeftArrowIcon: () => (
                <ArrowIconContainer>
                  <LeftArrowIcon />
                </ArrowIconContainer>
              ),
              SwitchViewIcon: () => (
                <ArrowIconContainer>
                  <SwitchArrowIcon />
                </ArrowIconContainer>
              )
            }}
            componentsProps={{
              ...(isClearable && {
                actionBar: {
                  actions: ['clear']
                }
              })
            }}
            PaperProps={{
              // Applying styles for the desktop version
              sx: (theme: Theme) => styles(theme)
            }}
            DialogProps={{
              // Applying styles for the mobile version
              sx: (theme: Theme) => styles(theme)
            }}
            onError={(errorObj) => {
              console.log("error", errorObj);
              this.state.touched && this.setState({ errorType: errorObj })
              this.state.touched && setErrorValue && setErrorValue(!!(errorObj !== null))
            }}
          />
          {error && (
            <img className={classes.stateIcon} src={ErrorIcon} alt="Invalid input icon" />
          )}
          {this.state.touched && !error && (
            <img className={classes.stateIcon} src={ValidIcon} alt="Valid input icon" />
          )}
        </FormControl>
      </LocalizationProvider>
    );
  }
}

export default withTranslate(withStyles(APInputStyles)(APDOBInput));